import { useState, useRef, useEffect } from 'react';

export default function LivePlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(70);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (audioRef.current && !audioContext && isPlaying) {
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const analyserNode = ctx.createAnalyser();
        analyserNode.fftSize = 256;
        
        const source = ctx.createMediaElementSource(audioRef.current);
        source.connect(analyserNode);
        analyserNode.connect(ctx.destination);
        
        setAudioContext(ctx);
        setAnalyser(analyserNode);
      } catch (err) {
        console.error('Error initializing audio context:', err);
      }
    }
  }, [audioContext, isPlaying]);

  useEffect(() => {
    if (analyser && canvasRef.current && isPlaying) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const draw = () => {
        if (!isPlaying) {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
          return;
        }
        
        animationFrameRef.current = requestAnimationFrame(draw);
        analyser.getByteFrequencyData(dataArray);

        ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const barWidth = (canvas.width / bufferLength) * 2.5;
        let x = 0;

        for (let i = 0; i < bufferLength; i++) {
          const barHeight = (dataArray[i] / 255) * canvas.height * 0.8;
          
          const gradient = ctx.createLinearGradient(0, canvas.height - barHeight, 0, canvas.height);
          gradient.addColorStop(0, '#d4af37');
          gradient.addColorStop(1, '#8b7355');
          
          ctx.fillStyle = gradient;
          ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
          
          x += barWidth + 1;
        }
      };

      draw();
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [analyser, isPlaying]);

  const togglePlay = async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      setError(null);
    } else {
      setIsLoading(true);
      setError(null);
      
      try {
        // Resume audio context if suspended
        if (audioContext && audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        
        // Set volume before playing
        audioRef.current.volume = volume / 100;
        
        // Try to play
        const playPromise = audioRef.current.play();
        
        if (playPromise !== undefined) {
          await playPromise;
          setIsPlaying(true);
          setError(null);
        }
      } catch (err: any) {
        console.error('Error playing audio:', err);
        setIsPlaying(false);
        
        // Set user-friendly error message
        if (err.name === 'NotAllowedError') {
          setError('Por favor, haz clic de nuevo para reproducir');
        } else if (err.name === 'NotSupportedError') {
          setError('Tu navegador no soporta este formato de audio');
        } else if (err.name === 'AbortError') {
          setError('Reproducción interrumpida. Intenta de nuevo');
        } else {
          setError('Error al conectar con la transmisión. Verifica tu conexión');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100;
    }
  };

  // Handle audio errors
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleError = () => {
      setIsPlaying(false);
      setIsLoading(false);
      setError('No se pudo cargar la transmisión. Verifica la URL del stream');
    };

    const handleCanPlay = () => {
      setError(null);
    };

    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, []);

  return (
    <div className="bg-black/40 backdrop-blur-md border border-[#d4af37]/30 rounded-2xl p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto">
      <audio 
        ref={audioRef} 
        src="https://app0102.sonicpanelradio.com/hitscolletions/stream"
        preload="none"
        crossOrigin="anonymous"
      />
      
      {/* Visualizer */}
      <div className="mb-4 sm:mb-6 rounded-lg overflow-hidden bg-black/50">
        <canvas 
          ref={canvasRef} 
          width="600" 
          height="120" 
          className="w-full h-20 sm:h-24 lg:h-32"
        />
      </div>

      {/* Now Playing Info */}
      <div className="text-center mb-4 sm:mb-6">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500 animate-pulse"></div>
          <span className="text-[#d4af37] font-semibold text-xs sm:text-sm uppercase tracking-wider">
            EN VIVO
          </span>
        </div>
        <h3 className="text-white text-base sm:text-lg lg:text-xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
          Música las 24 horas
        </h3>
        <p className="text-white/60 text-xs sm:text-sm">Los mejores éxitos sin parar</p>
        
        {/* Error Message */}
        {error && (
          <div className="mt-3 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-xs sm:text-sm">{error}</p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6">
        <button 
          onClick={togglePlay}
          disabled={isLoading}
          className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-[#d4af37] rounded-full flex items-center justify-center hover:shadow-lg hover:shadow-[#d4af37]/50 transition-all duration-300 cursor-pointer group disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <i className="ri-loader-4-line text-2xl sm:text-3xl lg:text-4xl text-black animate-spin"></i>
          ) : (
            <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-2xl sm:text-3xl lg:text-4xl text-black group-hover:scale-110 transition-transform`}></i>
          )}
        </button>
      </div>

      {/* Volume Control */}
      <div className="flex items-center gap-3 sm:gap-4 px-2 sm:px-4">
        <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
          <i className="ri-volume-up-fill text-[#d4af37] text-base sm:text-lg lg:text-xl"></i>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={volume}
          onChange={handleVolumeChange}
          className="flex-1 h-1 sm:h-2 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 sm:[&::-webkit-slider-thumb]:w-4 sm:[&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#d4af37] [&::-webkit-slider-thumb]:cursor-pointer"
          style={{
            background: `linear-gradient(to right, #d4af37 0%, #d4af37 ${volume}%, rgba(255,255,255,0.2) ${volume}%, rgba(255,255,255,0.2) 100%)`
          }}
        />
        <span className="text-white/60 text-xs sm:text-sm font-medium min-w-[2rem] sm:min-w-[2.5rem] text-right">{volume}%</span>
      </div>
    </div>
  );
}
